/**
 * Helpers for client-side asset downloads.
 *
 * Supports:
 *  - Direct file downloads (PNG, SVG, ZIP, etc.) via blob/href
 *  - SVG → PNG rasterisation in the browser (so we can ship one SVG
 *    template and produce multiple resolutions on demand)
 *  - HTML element → PNG snapshot (for live-preview templates)
 *  - Copy text to clipboard with toast
 */
import { toast } from "sonner";

/**
 * Trigger a browser download for any text/binary blob.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Download a file already hosted at a URL (e.g. /easy-gutachter-logo.png).
 * Uses a fetch+blob round-trip so the `download` attribute reliably renames
 * the file on cross-origin/edge cases.
 */
export async function downloadFromUrl(url, filename) {
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("HTTP " + res.status);
    const blob = await res.blob();
    downloadBlob(blob, filename);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("download failed:", e);
    toast.error("Download fehlgeschlagen — bitte erneut versuchen");
  }
}

/**
 * Rasterise an SVG string into a PNG and download it.
 * Width/height in pixels; defaults to the SVG's intrinsic viewBox size.
 */
export async function svgStringToPngDownload(svgString, filename, { width, height, background = "#ffffff" } = {}) {
  // Inline images need to be base64 — fetch and inline any <image href="/...">
  const inlined = await inlineSvgImageRefs(svgString);
  const blob = new Blob([inlined], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = width || img.naturalWidth;
      const h = height || img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (background) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, w, h);
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((png) => {
        if (!png) return reject(new Error("canvas → PNG failed"));
        downloadBlob(png, filename);
        resolve();
      }, "image/png", 0.95);
      URL.revokeObjectURL(url);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/** Replace any <image href="/file.png"/> references inside an SVG with
 *  base64-inline data URIs so the SVG stays self-contained for rasterising. */
async function inlineSvgImageRefs(svgString) {
  const regex = /href="(\/[^"]+\.(png|jpe?g|svg))"/g;
  const refs = Array.from(svgString.matchAll(regex)).map((m) => m[1]);
  if (refs.length === 0) return svgString;
  const replacements = await Promise.all(refs.map(async (path) => {
    try {
      const res = await fetch(path);
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return [path, dataUrl];
    } catch {
      return [path, path];
    }
  }));
  let out = svgString;
  for (const [original, dataUrl] of replacements) {
    out = out.replaceAll(`href="${original}"`, `href="${dataUrl}"`);
  }
  return out;
}

/** Save an SVG string as an .svg file. */
export function downloadSvgString(svgString, filename) {
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  downloadBlob(blob, filename);
}

/** Copy a piece of text to the clipboard and show a toast confirmation. */
export async function copyToClipboard(text, label = "Kopiert") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    // fallback for very old browsers
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    toast.success(label);
  }
}
